import React, { useState, useEffect, useCallback } from 'react';
import axios from "axios";
import { useNavigate, createSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import RenderChart from "./RenderChart";
import Tooltip from '@mui/material/Tooltip';
import { useAuth } from "../../../context/AuthProvider";
import { ADMIN_USERS, MARKET_DASHBOARD_COMPONENTS, MARKET_DASHBOARD_DAYS, BASE_URL } from '../../../constant/index';
import { getMdbData, processMarketData } from "../../../utils/getMdbData";
import RestrictedScreenNotice from '../../Common/RestrictedScreenNotice';
import useIsSmallScreen from '../../../hooks/useIsSmallScreen';

const MarketDashboard = ({ currentTab }) => {
	const navigate = useNavigate();
	const { token, userEmail, logOut } = useAuth();
    const isSmallScreen = useIsSmallScreen();

	const [ isLoading, setLoading ] = useState(false);
	const [ errorMsg, setErrorMsg ] = useState('');
	const [ topLabels, setTopLabels ] = useState([]);
	const [ topData, setTopData ] = useState([]);
	const [ component, setSelectedComponents ] = useState('Nifty 50');
	const [ day, setSelectedDay ] = useState('1-Day');
	const [ arStockDataMaster, setArStockDataMaster ] = useState([]);
	const [ arMdbData, setArMdbData ] = useState([]);
	const [ chartCategory, setChartCategory ] = useState('top');
	const [ lineChartData, setLineChartData] = useState([]);
	const [ lineChartLabels, setLineChartLabels] = useState([]);
	const canViewLineChart = ADMIN_USERS.includes(userEmail);

	const handleServerOrSessionError = useCallback((error) => {
		const response = error?.response;
		const responseMessage = response?.data?.msg;
		const isSessionExpired =
			response?.status === 401 ||
			response?.status === 403 ||
			response?.data?.statusCode === 503 ||
			responseMessage === 'Your session has expired. Please logout and login again.';

		if (isSessionExpired) {
			logOut('/sign-in');
			return;
		}

		setErrorMsg('Server encountered an issue. Please try again later.');
	}, [logOut]);

	const getLineChart = useCallback(async (symbols, duration) => {
		try {
			const response = await axios.post(BASE_URL+'/getLineChartData', {
				symbols, duration
			}, {
				headers: {
					"Authorization": `Bearer ${token}`
				},
			});
		  	return response.data;
		} catch (e) {
		  handleServerOrSessionError(e);
		  return { xLabels: [], series: [] };
		}
	}, [handleServerOrSessionError, token]);

	const loadDataForMDB = async (component, day, category = chartCategory) => {
		const { topLabels, topData } = getMdbData(arStockDataMaster, arMdbData, component, day, category);
		setTopLabels(topLabels);
		setTopData(topData);
		setLineChartData([]);
		setLineChartLabels([]);

		if (canViewLineChart && topLabels.length > 0) {
			const { xLabels, series } = await getLineChart(topLabels, day);
			setLineChartData(series);
			setLineChartLabels(xLabels);
		}
	};

	const getMarketDashboardData = async () => {
		setLoading(true);
		try {
			const response = await axios.post(BASE_URL+'/getMDB', {}, {
				headers: {
					"Authorization": `Bearer ${token}`
				},
			});
	
			const res = await response.data;
			if (res?.statusCode === 503) {
				logOut('/sign-in');
				return;
			}
	
			if (res?.arStockDataMaster && res?.arStockMarketData?.length > 0) {
				setTopLabels([]); 
				setTopData([]);
				const { arStockDataMaster, arStockMarketData, dateObj } = res;
				const { topLabels, topData, transformed } = processMarketData(arStockMarketData, dateObj, arStockDataMaster, component, day, chartCategory);
				setTopLabels(topLabels);
				setTopData(topData);
				setLineChartData([]);
				setLineChartLabels([]);
				setArStockDataMaster(arStockDataMaster);
				setArMdbData(transformed);

				if (canViewLineChart && topLabels.length > 0) {
					const { xLabels, series } = await getLineChart(topLabels, day);
					setLineChartData(series);
					setLineChartLabels(xLabels);
				}
			}
		} catch (err) {
		  handleServerOrSessionError(err);
		} finally {
		  setLoading(false);
		}
	};
	
	useEffect(() => {
		getMarketDashboardData();
	}, [token]);
	
	useEffect(() => {
		const params = { component, day };
		navigate({
			search: `?${createSearchParams(params)}`
		});
	}, [component, day, currentTab]);

	const onComponentSelection = async (value) => {
		setTopLabels([]);
		setTopData([]);
		setChartCategory('top');
		setSelectedComponents(value);
		await loadDataForMDB(value, day);
	}

	const onDaySelection = async (value) => {
		setTopLabels([]);
		setTopData([]);
		setSelectedDay(value);
		await loadDataForMDB(component, value);
	}

	const onCategoryChange = async (e) => {
		setChartCategory(e.target.value);
		await loadDataForMDB(component, day, e.target.value);
	}

	if (!userEmail) {
		return (
			<div className="container error-block">
				<div className="hr" />
				<div className="row">
					<div className="col-12">
						<h1 className="page-title-master">Market Dashboard</h1>
					</div>

					<Stack sx={{ width: '100%' }} spacing={2}>
						<Alert severity="error">This is member-only section. Please login.</Alert>
					</Stack>
				</div>
			</div>
		)
	}

    if (isSmallScreen) {
        return (
            <div className="container restricted-screen-layout">
                <div className="hr" />
                <RestrictedScreenNotice title="Market Dashboard" />
            </div>
        );
    }

	return (
		<div className="container">
			<div className="row">
				<div className="col-4">
					<div className="md-links" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 0.6fr)', gap: '2px' }}>
						{MARKET_DASHBOARD_COMPONENTS.map(({ key, value, label }) => (
							<button 
								key={key} 
								onClick={() => onComponentSelection(value)} 
								className={`btn mdb-button-width ${value === component ? 'btn-primary button-cus' : 'btn-outline-secondary'}`}
							>
								{label}
							</button>
						))}
					</div>
				</div>
				<div className="col-8">
					<div className="days-links">
						{
							MARKET_DASHBOARD_DAYS.map(({ key, value, label, tooltipContent }) => {
								return (
									<Tooltip key={key} title={tooltipContent || ''}>
										<button className={"btn " + (value === day ? 'btn-primary button-cus' : 'btn-outline-secondary')} onClick={() => onDaySelection(value)}>{label}</button>
									</Tooltip>
								)
							}) 
						}
					</div>
					<div style={{ 'paddingTop': '10px' }}>
						<RadioGroup
							row
							aria-labelledby="row-radio-buttons-group-label"
							name="row-radio-buttons-group"
							onChange={onCategoryChange}
							value={chartCategory}
						>
							<FormControlLabel value="top" control={<Radio disabled={component === 'Sectoral Indices'} />} label="Top 10" />
							<FormControlLabel value="bottom" control={<Radio disabled={component === 'Sectoral Indices'} />} label="Bottom 10" />
							{
								component === 'Nifty 50' && 
								<FormControlLabel value="all" control={<Radio />} label="Show All" />
							}
						</RadioGroup>
					</div>

					<RenderChart 
						errorMsg={errorMsg}
						isLoading={isLoading} 
						topLabels={topLabels} 
						topData={topData} 
						day={day} 
						component={component}
						lineChartData={lineChartData}
						lineChartLabels={lineChartLabels}
						chartCategory={chartCategory}
					/>	
				</div>
			</div>
		</div>        
	)
}

export default MarketDashboard;
